import React, {useMemo} from 'react';
import styled from 'styled-components';
import {useIntl} from 'react-intl';
import {Detail, TextField} from '@forecast-it/design-system';
import {NovaAgentDraft} from '../state/useNovaAgentDraft';
import {PromptTarget} from '../state/useNovaPromptState';
import {NovaVariable} from '../shared/novaVariableLibrary';
import {NovaModel} from '../shared/novaModelList';
import {NovaIssueKind} from '../shared/novaInputValidation';
import {MonospacePromptEditor} from './MonospacePromptEditor';
import {NovaWorkflowModelSelect} from './NovaModelPicker';
import {NovaInputIssues} from './NovaInputIssues';
import {NovaReferenceFile} from './useNovaReferenceFiles';
import {NovaWorkflowReferenceFiles} from './NovaWorkflowReferenceFiles';
import {NovaMcpTools} from './NovaMcpTools';
import {NovaWorkflowVariableLibrary, StudioWorkflowVariableGroup, validateWorkflowInput} from './NovaWorkflowVariableLibrary';
import {usePromptTabTargetClaim} from './usePromptTargetClaim';
import {useNovaMcpTools} from '../shared/useNovaMcpTools';

// Design system spacing step 500: a workflow's sections are fewer and shorter than a task agent's.
const Container = styled.div`
	display: flex;
	flex-direction: column;
	flex-grow: 1;
	min-height: 0;
	gap: 20px;
`;

const VersionNameColumn = styled.div`
	display: flex;
	flex-direction: column;
	gap: 4px;
`;

// The User prompt is the shorter of the two; the System prompt holds the standing role and rules.
const USER_PROMPT_HEIGHT = 160;
const SYSTEM_PROMPT_HEIGHT = 280;

// The service refuses a longer name; the field stops at this many characters.
export const VERSION_NAME_MAX_LENGTH = 60;

interface Props {
	draft: NovaAgentDraft;
	// The workflow's declared variables, in declared order.
	variableGroups: StudioWorkflowVariableGroup[];
	// The declaration read failed, so the library says so and flags no declared token as unknown.
	declarationFailed: boolean;
	// The declaration has not been read yet: no token is flagged and the library claims no count.
	declarationLoading?: boolean;
	// Reads the declaration again after a failed read.
	onRetryDeclaration?: () => void;
	// The workflow's ready files, each citable as a {file:...} variable, among the library variables.
	variables: NovaVariable[];
	models: NovaModel[];
	referenceFiles: NovaReferenceFile[];
	onAttachFile: (file: File, onProgress?: (fraction: number) => void) => Promise<void>;
	onRemoveFile: (fileId: string) => Promise<void>;
}

// The name of the workflow version being edited. Part of the draft, so typing in it marks the agent as
// changed and Save sends it with the rest.
function NovaVersionNameField({draft}: {draft: NovaAgentDraft}): JSX.Element {
	const {formatMessage} = useIntl();
	return (
		<VersionNameColumn>
			<TextField
				label={formatMessage({id: 'nova.prompt.versionNameLabel'})}
				data-cy="nova-prompt-version-name"
				placeholder={formatMessage({id: 'nova.prompt.versionNamePlaceholder'})}
				value={draft.versionName}
				onValueChange={(value: string) => draft.setVersionName(value)}
				maxLength={VERSION_NAME_MAX_LENGTH}
				size={TextField.SIZE.LARGE}
				width={TextField.WIDTH.FLEX}
			/>
			<Detail color="auxiliary" data-cy="nova-prompt-version-name-helper">
				{formatMessage({id: 'nova.prompt.versionNameHelper'})}
			</Detail>
		</VersionNameColumn>
	);
}

// An agentic workflow's Prompt tab. A workflow is bound to one page and never picked from a list, so
// it has no Title, Description, Category or Test agent panel; it leads with the version name, then
// the model, its files, its declared variables and the MCP tools, then the two prompts. Bound to the
// same working draft as the other tabs, and committed by the header Save.
export function NovaWorkflowPromptTab({
	draft,
	variableGroups,
	declarationFailed,
	declarationLoading = false,
	onRetryDeclaration,
	variables,
	models,
	referenceFiles,
	onAttachFile,
	onRemoveFile,
}: Props): JSX.Element {
	const {formatMessage} = useIntl();
	const {values, setValue, setActiveTarget, activeTarget, insertTokenAtCursor, lastInsertion} = draft.promptState;
	usePromptTabTargetClaim(draft.promptState);
	const mcpTools = useNovaMcpTools();
	const declarationUnknown = declarationFailed || declarationLoading;
	// Both libraries name the prompt a click inserts into: the System prompt while it is the one being
	// edited, otherwise the User prompt.
	const targetLabel = formatMessage({
		id: activeTarget === PromptTarget.System ? 'nova.variableLibrary.targetSystem' : 'nova.variableLibrary.targetUser',
	});

	// A freshly opened workflow may have both prompts empty, so a "required" issue is held back until
	// the author has started editing.
	const showRequired = draft.isDirty;
	const fileVariables = useMemo(() => variables.filter(variable => variable.scope === 'FILES'), [variables]);
	const userPromptIssues = useMemo(
		() =>
			validateWorkflowInput(
				values.userPromptTemplate,
				PromptTarget.User,
				variableGroups,
				fileVariables,
				declarationUnknown,
				mcpTools
			).filter(issue => showRequired || issue.kind !== NovaIssueKind.EmptyRequired),
		[values.userPromptTemplate, variableGroups, fileVariables, declarationUnknown, mcpTools, showRequired]
	);
	const systemPromptIssues = useMemo(
		() =>
			validateWorkflowInput(
				values.systemPrompt,
				PromptTarget.System,
				variableGroups,
				fileVariables,
				declarationUnknown,
				mcpTools
			).filter(issue => showRequired || issue.kind !== NovaIssueKind.EmptyRequired),
		[values.systemPrompt, variableGroups, fileVariables, declarationUnknown, mcpTools, showRequired]
	);

	return (
		<Container data-cy="nova-prompt-tab" data-testid="nova-prompt-tab">
			<NovaVersionNameField draft={draft} />
			<NovaWorkflowModelSelect
				models={models}
				value={draft.model}
				onChange={model => draft.setModel(model)}
				dataCy="nova-prompt-model"
			/>
			<NovaWorkflowReferenceFiles files={referenceFiles} onAttach={onAttachFile} onRemove={onRemoveFile} />
			<NovaWorkflowVariableLibrary
				variableGroups={variableGroups}
				fileVariables={fileVariables}
				loadFailed={declarationFailed}
				loading={declarationLoading}
				onRetry={onRetryDeclaration}
				helper={formatMessage({id: 'nova.variableLibrary.hint'})}
				targetLabel={targetLabel}
				onInsert={token => insertTokenAtCursor(token)}
			/>
			<NovaMcpTools
				mcpTools={mcpTools}
				onRetry={mcpTools.retry}
				helper={formatMessage({id: 'nova.mcpTools.hint'})}
				targetLabel={targetLabel}
				onInsert={token => insertTokenAtCursor(token)}
			/>
			<MonospacePromptEditor
				label={formatMessage({id: 'nova.prompt.userLabel'})}
				placeholder={formatMessage({id: 'nova.prompt.workflowUserPlaceholder'})}
				dataCy="nova-user-prompt"
				target={PromptTarget.User}
				value={values.userPromptTemplate}
				insertion={lastInsertion?.target === PromptTarget.User ? lastInsertion : null}
				height={USER_PROMPT_HEIGHT}
				monospace={false}
				onChange={(value: string) => setValue(PromptTarget.User, value)}
				onActivate={setActiveTarget}
				issues={
					userPromptIssues.length > 0 ? (
						<NovaInputIssues issues={userPromptIssues} dataCy="nova-user-prompt-issues" />
					) : undefined
				}
			/>
			<MonospacePromptEditor
				label={formatMessage({id: 'nova.prompt.systemLabel'})}
				placeholder={formatMessage({id: 'nova.prompt.systemPlaceholder'})}
				dataCy="nova-system-prompt"
				target={PromptTarget.System}
				value={values.systemPrompt}
				insertion={lastInsertion?.target === PromptTarget.System ? lastInsertion : null}
				height={SYSTEM_PROMPT_HEIGHT}
				monospace={false}
				onChange={(value: string) => setValue(PromptTarget.System, value)}
				onActivate={setActiveTarget}
				issues={
					systemPromptIssues.length > 0 ? (
						<NovaInputIssues issues={systemPromptIssues} dataCy="nova-system-prompt-issues" />
					) : undefined
				}
			/>
		</Container>
	);
}
